import React, { useState } from 'react';
import { Copy, Check } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { useTranslation } from 'react-i18next';

interface DocumentShareDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  shareUrl: string | null;
  expiresIn?: number;
  documentName?: string;
}

export const DocumentShareDialog: React.FC<DocumentShareDialogProps> = ({
  open,
  onOpenChange,
  shareUrl,
  expiresIn,
  documentName,
}) => {
  const { toast } = useToast();
  const { t } = useTranslation(['pages.projectDetails']);
  const [copied, setCopied] = useState(false);

  const handleCopyLink = async () => {
    if (!shareUrl) return;

    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast({
        title: t('documents.linkCopied'),
        variant: 'default',
      });
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy link:', error);
      toast({
        title: t('toasts.error'),
        description: t('documents.shareError'),
        variant: 'destructive',
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('documents.shareTitle')}</DialogTitle>
          <DialogDescription>
            {documentName && (
              <span className="block mt-1 text-sm text-muted-foreground">
                {documentName}
              </span>
            )}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div>
            <label className="text-sm font-medium mb-2 block">
              {t('documents.shareLink')}
            </label>
            <div className="flex items-center gap-2">
              <input
                type="text"
                readOnly
                value={shareUrl || ''}
                className="flex-1 textbox-base text-xs"
                onClick={(e) => (e.target as HTMLInputElement).select()}
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleCopyLink}
                className="whitespace-nowrap"
              >
                {copied ? (
                  <>
                    <Check className="h-4 w-4 mr-2" />
                    {t('documents.linkCopied')}
                  </>
                ) : (
                  <>
                    <Copy className="h-4 w-4 mr-2" />
                    {t('documents.copyLink')}
                  </>
                )}
              </Button>
            </div>
          </div>
          {expiresIn && (
            <p className="text-xs text-muted-foreground">
              {t('documents.expiresIn')} {expiresIn} {t('documents.hours')}
            </p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

